import { Component, DestroyRef, OnDestroy, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { getSelectedAdminPlan } from '../login/store/selectors';
import { ILoginState } from '../login/store/state';
import { AppConfig } from '../shared/app.config';
import { IBmtBillingState } from '../store/billing/state';
import { BmtBillingAsync } from '../store/billing/async';
import { getEbillError } from '../store/billing/selector';
import { IAppAuthorizations, IUiAuthorizations } from '../authorizations/state';
import { getRelaxedUiAuth } from '../authorizations/selector';
import { BillingEbillContext } from './billing.component';

@Component({
  selector: 'app-billing',
  template: `
    <app-billing-page
      [selectedAdminPlan]="selectedAdminPlan()"
      [ebillError]="ebillError()"
      [ebillResetVersion]="ebillResetVersion()"
      [billingAttributeFeatures]="billingAttributeFeatures()"
      (insertEbillContext)="insertEbillContext($event)"></app-billing-page>
  `,
  standalone: false
})
export class BillingContainer implements OnDestroy {
  luxId = 'BMT-Billing-Comp' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);
  readonly ebillResetVersion = signal(0);

  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly ebillError = toSignal(this.lux.get(getEbillError, this.luxId), { initialValue: '' });
  readonly relaxedAuth = toSignal(this.lux.get(getRelaxedUiAuth, this.luxId), {
    initialValue: {} as IUiAuthorizations | {}
  });
  readonly billingAttributeFeatures = computed(() => (this.relaxedAuth() as IUiAuthorizations)?.billing || []);

  constructor(
    private readonly billingAsync: BmtBillingAsync,
    private readonly lux: Lux<{ billingState: IBmtBillingState, login: ILoginState, appUiAuth: IAppAuthorizations }>,
    private readonly env: AppConfig
  ) {}

  insertEbillContext(ebillUserContext: BillingEbillContext): void {
    const req = {
      ebillUserContext
    };
    this.lux.set(state => state.billingState, { ebillRequest: req });
    this.billingAsync.insertEbillContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        if (response && response.status === 201) {
          const url = this.toSafeWebUrl(this.env.peopleSoftUrl);
          if (url) {
            const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
            if (openedWindow) {
              openedWindow.opener = null;
            }
          }
          this.ebillResetVersion.update(version => version + 1);
        }
      });
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }

  private toSafeWebUrl(candidateUrl: string | null | undefined): string {
    if (!candidateUrl) {
      return '';
    }

    try {
      const resolvedUrl = new URL(candidateUrl, window.location.origin);
      return ['http:', 'https:'].includes(resolvedUrl.protocol) ? resolvedUrl.toString() : '';
    } catch {
      return '';
    }
  }
}
